import { createContext, useContext, useState, type ReactNode } from 'react';
import { apiClient } from './api/client';

const DEFAULT_USERNAME = 'admin';
const DEFAULT_PASSWORD = 'replya2024';
const PREFS_USERNAME = 'saved_username';
const PREFS_API_KEY = 'saved_admin_api_key';

interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  adminApiKey: string | null;
}

interface AuthContextValue extends AuthState {
  login: (
    username: string,
    password: string,
    options?: { rememberMe?: boolean; adminApiKey?: string },
  ) => boolean;
  logout: () => void;
  hasSavedCredentials: () => boolean;
  updateAdminApiKey: (key: string | null) => void;
}

function tryAuthenticate(username: string, password: string, adminApiKey?: string): boolean {
  if (username === DEFAULT_USERNAME && password === DEFAULT_PASSWORD) {
    apiClient.setAdminApiKey(adminApiKey ?? null);
    return true;
  }
  return false;
}

function readInitialState(): AuthState {
  const devApiKey = import.meta.env.DEV ? import.meta.env.VITE_ADMIN_API_KEY : undefined;
  if (devApiKey && tryAuthenticate(DEFAULT_USERNAME, DEFAULT_PASSWORD, devApiKey)) {
    return {
      isAuthenticated: true,
      username: DEFAULT_USERNAME,
      adminApiKey: devApiKey,
    };
  }

  try {
    const savedUsername = localStorage.getItem(PREFS_USERNAME);
    if (savedUsername === DEFAULT_USERNAME) {
      const savedApiKey = localStorage.getItem(PREFS_API_KEY);
      apiClient.setAdminApiKey(savedApiKey);
      return {
        isAuthenticated: true,
        username: savedUsername,
        adminApiKey: savedApiKey,
      };
    }
  } catch {
    // localStorage may be unavailable in restricted contexts.
  }

  return { isAuthenticated: false, username: null, adminApiKey: null };
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(readInitialState);

  const login: AuthContextValue['login'] = (username, password, options = {}) => {
    const ok = tryAuthenticate(username, password, options.adminApiKey);
    if (!ok) return false;

    setState({
      isAuthenticated: true,
      username,
      adminApiKey: options.adminApiKey ?? null,
    });

    if (options.rememberMe) {
      localStorage.setItem(PREFS_USERNAME, username);
      if (options.adminApiKey) {
        localStorage.setItem(PREFS_API_KEY, options.adminApiKey);
      } else {
        localStorage.removeItem(PREFS_API_KEY);
      }
    } else {
      localStorage.removeItem(PREFS_USERNAME);
      localStorage.removeItem(PREFS_API_KEY);
    }
    return true;
  };

  const logout = () => {
    apiClient.setAdminApiKey(null);
    setState({ isAuthenticated: false, username: null, adminApiKey: null });
    localStorage.removeItem(PREFS_USERNAME);
    localStorage.removeItem(PREFS_API_KEY);
  };

  const hasSavedCredentials = () => {
    return Boolean(localStorage.getItem(PREFS_USERNAME));
  };

  const updateAdminApiKey = (key: string | null) => {
    apiClient.setAdminApiKey(key);
    setState((s) => ({ ...s, adminApiKey: key }));
    const savedUsername = localStorage.getItem(PREFS_USERNAME);
    if (savedUsername) {
      if (key) {
        localStorage.setItem(PREFS_API_KEY, key);
      } else {
        localStorage.removeItem(PREFS_API_KEY);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, hasSavedCredentials, updateAdminApiKey }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
